/**
 * Cart utility functions for the Better Boom theme
 * 
 * This file contains functions for cart operations like adding products,
 * updating quantities, and showing cart notifications.
 */

class CartUtility {
  /**
   * Add a product variant to the cart
   * @param {number} variantId - The variant ID to add
   * @param {number} quantity - The quantity to add (default: 1)
   * @param {object} properties - Optional line item properties
   * @returns {Promise} - Resolves with the updated cart data
   */
  static async addToCart(variantId, quantity = 1, properties = null) {
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };
      
      // Add properties if provided
      if (properties) {
        formData.items[0].properties = properties;
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Dispatch event for other components to listen to
        document.dispatchEvent(new CustomEvent('cart:added', { 
          detail: { 
            item: data,
            cart: await this.getCartData()
          } 
        }));
        
        // Show notification
        this.showNotification('Product added to cart!', 'success');
        
        return data;
      } else {
        this.showNotification(data.description || 'Error adding to cart', 'error');
        throw new Error(data.description);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding to cart', 'error');
      throw error;
    }
  }
  
  /**
   * Get current cart data
   * @returns {Promise} - Resolves with the cart data
   */
  static async getCartData() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error getting cart data:', error);
      throw error;
    }
  }
  
  /**
   * Update cart item quantity
   * @param {number} lineItemKey - The line item key
   * @param {number} quantity - The new quantity
   * @returns {Promise} - Resolves with the updated cart data
   */
  static async updateQuantity(lineItemKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Dispatch event for other components to listen to
        document.dispatchEvent(new CustomEvent('cart:updated', { 
          detail: { cart: data } 
        }));
        
        return data;
      } else {
        this.showNotification(data.description || 'Error updating cart', 'error');
        throw new Error(data.description);
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }
  
  /**
   * Show a notification to the user
   * @param {string} message - The message to show
   * @param {string} type - The type of notification (success, error, info)
   */
  static showNotification(message, type = 'info') {
    // Create notification element if it doesn't exist
    let notification = document.getElementById('cart-notification');
    
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'cart-notification';
      notification.className = 'responsive-notification';
      document.body.appendChild(notification);
    }
    
    // Add appropriate class based on type
    notification.className = `responsive-notification responsive-notification-${type}`;
    notification.textContent = message;
    
    // Show notification
    notification.classList.add('responsive-notification-visible');
    
    // Hide after 3 seconds
    setTimeout(() => {
      notification.classList.remove('responsive-notification-visible');
    }, 3000);
  }
}

// Make CartUtility available globally
window.CartUtility = CartUtility;
